import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Download, Plus } from "lucide-react";
import api, { formatApiError } from "../api";
import { useApp } from "../context/AppContext";
import { Seo } from "../components/Seo";
import { StatusPill, VerificationBadge } from "../components/StatusPill";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const TABS = [
  ["overview", "Dashboard"], ["orders", "My Orders"], ["documents", "My Documents"],
  ["verifications", "Verification Reports"], ["payments", "Payments"], ["profile", "Profile"], ["support", "Support"],
];

export default function Dashboard() {
  const { user, t } = useApp();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [vers, setVers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [profile, setProfile] = useState({ name: "", phone: "" });
  const [ticket, setTicket] = useState({ category: "General", message: "", order_id: "" });

  useEffect(() => {
    if (user === false) { navigate("/login"); return; }
    if (!user) return;
    setProfile({ name: user.name || "", phone: user.phone || "" });
    api.get("/dashboard").then((r) => setStats(r.data)).catch(() => {});
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/invoices").then((r) => setInvoices(r.data)).catch(() => {});
    api.get("/verifications").then((r) => setVers(r.data)).catch(() => {});
    api.get("/tickets").then((r) => setTickets(r.data)).catch(() => {});
  }, [user, navigate]);

  const download = async (orderId, file) => {
    try {
      const r = await api.get(`/orders/${orderId}/download/${file.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = file.name; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Not available yet"); }
  };

  const saveProfile = async () => {
    try { await api.put("/auth/profile", profile); toast.success("Profile saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const createTicket = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tickets", ticket);
      toast.success("Ticket created");
      setTicket({ category: "General", message: "", order_id: "" });
      const { data } = await api.get("/tickets");
      setTickets(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const input = "w-full h-11 rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring";
  const deliverables = orders.flatMap((o) => (o.deliverables || []).map((f) => ({ ...f, order_id: o.order_id, item: o.item_name, status: o.status })));

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 pb-24 md:pb-10" data-testid="dashboard-page">
      <Seo title="Dashboard" description="Your DocGalaxy dashboard." />
      <h1 className="font-display font-bold text-2xl md:text-3xl">Namaste, {user?.name}</h1>
      <div className="mt-5 flex gap-2 overflow-x-auto pb-1" data-testid="dashboard-tabs">
        {TABS.map(([id, label]) => (
          <button key={id} onClick={() => setParams({ tab: id })} data-testid={`tab-${id}`}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${tab === id ? "bg-primary text-white" : "bg-muted text-muted-foreground hover:text-foreground"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" && stats && (
        <div className="mt-6" data-testid="dashboard-overview">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[["Total Orders", stats.total_orders], ["Active", stats.active_orders], ["Delivered", stats.delivered], ["Total Spent", `₹${stats.spent.toLocaleString("en-IN")}`]].map(([l, v]) => (
              <div key={l} className="bg-white border border-border rounded-xl p-5" data-testid={`stat-${l.toLowerCase().replace(/\s/g, "-")}`}>
                <p className="text-xs text-muted-foreground">{l}</p>
                <p className="font-display font-bold text-2xl mt-1">{v}</p>
              </div>
            ))}
          </div>
          <Button className="mt-6 bg-secondary" onClick={() => navigate("/services")} data-testid="new-request-btn"><Plus size={16} className="mr-1" /> New Request</Button>
        </div>
      )}

      {tab === "orders" && (
        <div className="mt-6 space-y-3" data-testid="dashboard-orders">
          {orders.length === 0 && <p className="text-muted-foreground text-sm">No orders yet. <button className="text-primary underline" onClick={() => navigate("/services")}>Request a document</button></p>}
          {orders.map((o) => (
            <div key={o.id} className="bg-white border border-border rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`order-row-${o.order_id}`}>
              <div>
                <p className="font-semibold">{o.item_name}</p>
                <p className="text-xs text-muted-foreground font-mono">{o.order_id} · ₹{o.total}</p>
                <p className="text-xs text-muted-foreground" data-testid={`order-sro-${o.order_id}`}><span className="font-semibold">SRO:</span> {o.property_details?.sro || "—"}</p>
              </div>
              <div className="flex items-center gap-3">
                <StatusPill status={o.status} />
                <Button size="sm" variant="outline" onClick={() => navigate(`/order/${o.order_id}`)} data-testid={`track-${o.order_id}`}>{t("track")}</Button>
                {!o.paid && <Button size="sm" className="bg-secondary" onClick={() => navigate(`/order/${o.order_id}`)} data-testid={`pay-${o.order_id}`}>Pay</Button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "documents" && (
        <div className="mt-6 space-y-3" data-testid="dashboard-documents">
          {deliverables.length === 0 && <p className="text-muted-foreground text-sm">No documents delivered yet.</p>}
          {deliverables.map((f) => (
            <div key={f.id} className="bg-white border border-border rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="font-semibold text-sm">{f.name}</p>
                <p className="text-xs text-muted-foreground">{f.item} · {f.order_id}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => download(f.order_id, f)} data-testid={`doc-download-${f.id}`}><Download size={14} className="mr-1" /> {t("download")}</Button>
            </div>
          ))}
        </div>
      )}

      {tab === "verifications" && (
        <div className="mt-6 space-y-3" data-testid="dashboard-verifications">
          {vers.length === 0 && <p className="text-muted-foreground text-sm">No verification reports yet.</p>}
          {vers.map((v) => (
            <div key={v.id} className="bg-white border border-border rounded-xl p-4" data-testid={`ver-row-${v.tracking_id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-mono font-semibold">{v.tracking_id}</p>
                  <p className="text-xs text-muted-foreground">{v.district} {v.village} {v.survey_number && `· Survey ${v.survey_number}`}</p>
                </div>
                <VerificationBadge level={v.overall} />
              </div>
              <p className="text-sm mt-2">{v.note}</p>
            </div>
          ))}
        </div>
      )}

      {tab === "payments" && (
        <div className="mt-6 space-y-3" data-testid="dashboard-payments">
          {invoices.length === 0 && <p className="text-muted-foreground text-sm">No payments yet.</p>}
          {invoices.map((i) => (
            <div key={i.id} className="bg-white border border-border rounded-xl p-4 flex justify-between items-center" data-testid={`invoice-${i.invoice_no}`}>
              <div>
                <p className="font-semibold">{i.invoice_no}</p>
                <p className="text-xs text-muted-foreground">{i.item_name} · {i.created_at?.slice(0, 10)}</p>
              </div>
              <span className="font-bold text-primary">₹{i.amount.toLocaleString("en-IN")}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "profile" && (
        <div className="mt-6 max-w-md bg-white border border-border rounded-xl p-6" data-testid="dashboard-profile">
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold">Name</label>
              <input className={input} value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} data-testid="profile-name" />
            </div>
            <div>
              <label className="text-xs font-semibold">Phone</label>
              <input className={input} value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} data-testid="profile-phone" />
            </div>
            <div>
              <label className="text-xs font-semibold">Email</label>
              <input className={input} value={user?.email} disabled data-testid="profile-email" />
            </div>
            <Button onClick={saveProfile} className="bg-primary" data-testid="profile-save-btn">Save</Button>
          </div>
        </div>
      )}

      {tab === "support" && (
        <div className="mt-6 grid lg:grid-cols-2 gap-6" data-testid="dashboard-support">
          <form onSubmit={createTicket} className="bg-white border border-border rounded-xl p-6 space-y-4">
            <h3 className="font-display font-semibold">New support ticket</h3>
            <select className={input} value={ticket.category} onChange={(e) => setTicket({ ...ticket, category: e.target.value })} data-testid="ticket-category">
              {["General", "Order issue", "Payment", "Document quality", "Verification", "Other"].map((c) => <option key={c}>{c}</option>)}
            </select>
            <input className={input} placeholder="Order ID (optional)" value={ticket.order_id} onChange={(e) => setTicket({ ...ticket, order_id: e.target.value })} data-testid="ticket-order-id" />
            <textarea className={`${input} h-28 py-2`} required placeholder="Describe your issue" value={ticket.message} onChange={(e) => setTicket({ ...ticket, message: e.target.value })} data-testid="ticket-message" />
            <Button type="submit" className="bg-primary" data-testid="ticket-submit-btn">Submit Ticket</Button>
          </form>
          <div className="space-y-3">
            {tickets.map((tk) => (
              <div key={tk.id} className="bg-white border border-border rounded-xl p-4" data-testid={`ticket-${tk.ticket_no}`}>
                <div className="flex justify-between items-center">
                  <p className="font-mono text-sm font-semibold">{tk.ticket_no}</p>
                  <StatusPill status={tk.status} />
                </div>
                <p className="text-sm mt-1">{tk.message}</p>
                {(tk.replies || []).map((r, i) => (
                  <p key={i} className="text-xs mt-2 bg-muted rounded p-2"><strong>Support:</strong> {r.text}</p>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
